import { formatDate, formatTime } from './date-utils';
import { getFulfillmentType, getDistributionStatus } from './distribution';
import { FULFILLMENT_TYPES, FULFILLMENT_STATUSES } from '../constants/fulfillment';
import { IDistributionView, IDistributionViewLineItem } from '../models/active-orders';

/**
 * `getFulfillmentType` can only answer BOPIS or ship-to-home while OMS reports
 * `distributionType` as PICKUP or DELIVERY, so the BOSS, BODFS, appliance, and
 * MISC branches below cannot be reached today, and neither can the delivery
 * tracker path: OMS sends no `deliveryTrackerData`. They are kept rather than
 * deleted because the OMS follow-up for a granular fulfillment type would put
 * them straight back into use. Anything they read off the distribution comes
 * back undefined in the meantime, which the optional chaining already handles.
 */

// const getBODFSReservationWindow = (fg) => {
//   const start = fg?.bodfsDeliveryAndHandling?.bodfsDeliveries?.[0]?.reservationWindowStartTime;
//   const end = fg?.bodfsDeliveryAndHandling?.bodfsDeliveries?.[0]?.reservationWindowEndTime;
//   return { start, end };
// };

const getBODFSReservationWindow = (distribution?: IDistributionView) => {
  const delivery = distribution?.bodfsDeliveryAndHandling?.bodfsDeliveries?.[0];
  return {
    start: delivery?.reservationWindowStartTime,
    end: delivery?.reservationWindowEndTime,
  };
};

// const getApplianceDeliveryDate = (distribution) => {
//   return distribution?.shippingAndHandling?.applianceDeliveries?.[0]?.currentDeliveryDate
//     || distribution?.shippingAndHandling?.applianceDeliveries?.[0]?.originalDeliveryDate;
// };

const getApplianceDeliveryDate = (distribution?: IDistributionView) => {
  const applianceDelivery = distribution?.shippingAndHandling?.applianceDeliveries?.[0];
  return applianceDelivery?.currentDeliveryDate || applianceDelivery?.originalDeliveryDate;
};

const formatShortTime = (time?: string) => formatTime(time, '', true);

const formatDisplayDate = (date?: string) => ({
  long: date ? formatDate(date) : '',
  short: date ? formatDate(date, 'MMM d') : '',
});

const formatDisplayTimeWindow = (startTime?: string, endTime?: string) => {
  if (!startTime || !endTime) {
    return { long: '', short: '' };
  }

  return {
    long: `from ${formatTime(startTime)} to ${formatTime(endTime)}`,
    // \u2014 is the unicode for an em dash (—)
    short: `${formatShortTime(startTime)}\u2014${formatShortTime(endTime)}`,
  };
};

const orderStatus = (lineItems?: IDistributionViewLineItem[]) => {
  const activeItem = lineItems?.find(
    (item) => item?.statusDescription !== FULFILLMENT_STATUSES.CANCELED,
  );
  return activeItem ? activeItem?.statusDescription : lineItems?.[0]?.statusDescription;
};

const getDeliveryTrackerStatus = (distribution?: IDistributionView) => {
  const deliveryTrackerData = distribution?.deliveryTrackerData;
  const trackerStatus = deliveryTrackerData?.status;
  if (!trackerStatus) {
    return null;
  }

  const itemStatus = orderStatus(distribution?.lineItems);
  const deliveredDate = distribution?.lineItems?.[0]?.deliveredDateArray?.[0];
  const estimatedDeliveryWindowDate = deliveryTrackerData?.estimatedDeliveryWindow?.date;
  const deliveryWindow = deliveryTrackerData?.deliveryWindow;
  const estimatedPickupDate = deliveryTrackerData?.pickUpDetails?.estimatedPickupDate;

  const trackerStatusMap: {
    [key: string]: {
      statusText: string;
      deliveryStatusText: string;
      date?: { long: string; short: string };
      timeWindow?: { long: string; short: string };
    };
  } = {
    ARRIVED: {
      statusText: 'Arrived',
      deliveryStatusText: 'Arrived',
    },
    AWAITING_TRUCKLOAD: {
      statusText: 'Awaiting Truckload',
      deliveryStatusText: 'Awaiting Truckload',
    },
    IN_TRANSIT: {
      statusText: 'In Transit',
      deliveryStatusText: 'In Transit',
    },
    ON_THE_WAY: {
      statusText: 'On the Way',
      deliveryStatusText: 'On the Way',
    },
    ON_VEHICLE_FOR_DELIVERY: {
      statusText: 'On Vehicle for Delivery',
      deliveryStatusText: 'On Vehicle for Delivery',
    },
    MISSED_WITH_ATTEMPT: {
      statusText: 'Delivery Missed',
      deliveryStatusText: 'Delivery Missed',
    },
    MISSED_WITHOUT_ATTEMPT: {
      statusText: 'Delivery Missed without Attempt',
      deliveryStatusText: 'Delivery Missed without Attempt',
    },
    MISSED_WITHOUT_ENROUTE: {
      statusText: 'Delivery Missed without Enroute',
      deliveryStatusText: 'Delivery Missed without Enroute',
    },
    UNDELIVERABLE: {
      statusText: 'Undeliverable',
      deliveryStatusText: 'Undeliverable',
    },
    DELIVERED_WITH_EXCEPTION: {
      statusText: 'Delivered with Exception',
      deliveryStatusText: 'Delivered with Exception',
    },
    OUT_FOR_DELIVERY: {
      statusText: 'Arriving',
      date: formatDisplayDate(estimatedDeliveryWindowDate),
      deliveryStatusText: 'Out For Delivery',
    },
    SHIPPED: {
      statusText: 'Arriving',
      date: formatDisplayDate(estimatedDeliveryWindowDate),
      deliveryStatusText: `Arriving  By ${formatDate(estimatedDeliveryWindowDate)}`,
    },
    SCHEDULED: {
      statusText: 'Scheduled',
      deliveryStatusText: 'Scheduled',
    },
    READY_FOR_PICKUP: {
      statusText: 'Ready for Pickup',
      date: formatDisplayDate(estimatedPickupDate),
      deliveryStatusText: `Ready for Pickup ${formatDate(estimatedPickupDate)}`,
    },
    PICKED_UP: {
      statusText: 'Picked Up',
      deliveryStatusText: 'Picked Up',
    },
    DELAYED: {
      statusText: 'Delayed',
      deliveryStatusText: 'Delayed',
    },
  };

  if (trackerStatus === 'DELIVERED') {
    if (itemStatus === FULFILLMENT_STATUSES.DELIVERED) {
      return {
        statusText: 'Delivered',
        date: formatDisplayDate(deliveredDate),
        deliveryStatusText: `Delivered ${formatDate(deliveredDate, 'monthDay')}`,
      };
    }
    return {
      statusText: 'Delivered',
      date: formatDisplayDate(estimatedDeliveryWindowDate),
      deliveryStatusText: `Delivered ${formatDate(estimatedDeliveryWindowDate)}`,
    };
  }

  if (trackerStatus === 'ORDER_RECEIVED') {
    if (deliveryWindow?.date) {
      return {
        statusText: 'Arriving',
        date: formatDisplayDate(deliveryWindow?.date),
        timeWindow: formatDisplayTimeWindow(deliveryWindow?.startTime, deliveryWindow?.endTime),
        deliveryStatusText: `Arriving By ${formatDate(deliveryWindow?.date)}`,
      };
    }
    const fulfillmentDate = distribution?.distributionDate;
    return {
      statusText: 'Order Received',
      date: formatDisplayDate(fulfillmentDate),
      deliveryStatusText: `Order Received ${formatDate(fulfillmentDate)}`,
    };
  }

  return trackerStatusMap[trackerStatus] || null;
};

export const getOrderStatus = (distribution?: IDistributionView) => {
  const fulfillmentType = getFulfillmentType(distribution);
  const statusDescription = getDistributionStatus(distribution);

  const defaultStatus = {
    statusText: '',
    date: {
      long: '',
      short: '',
    },
    timeWindow: {
      long: '',
      short: '',
    },
    deliveryStatusText: '',
  };

  if (!fulfillmentType || !statusDescription) {
    return defaultStatus;
  }

  if (distribution?.deliveryTrackerData?.status) {
    const trackerStatus = getDeliveryTrackerStatus(distribution);
    if (trackerStatus) {
      return trackerStatus;
    }
  }

  // OMS sends one date per distribution, so every status here reads the same
  // field. The older shape carried a separate shipment estimate; nothing in the
  // API replaces it yet.
  const shipToHomeMap = () => {
    const deliveryDate = distribution?.distributionDate;
    return {
      [FULFILLMENT_STATUSES.BEING_PROCESSED]: {
        statusText: 'Estimated Arrival',
        date: formatDisplayDate(deliveryDate),
        deliveryStatusText: `Arriving By ${formatDate(deliveryDate)}`,
      },
      [FULFILLMENT_STATUSES.SHIPPED]: {
        statusText: 'Arriving',
        date: formatDisplayDate(deliveryDate),
        deliveryStatusText: `Arriving By ${formatDate(deliveryDate)}`,
      },
      [FULFILLMENT_STATUSES.OUT_FOR_DELIVERY]: {
        statusText: 'Arriving',
        date: formatDisplayDate(deliveryDate),
        deliveryStatusText: 'Out For Delivery',
      },
      [FULFILLMENT_STATUSES.DELIVERED]: {
        statusText: 'Delivered',
        date: formatDisplayDate(deliveryDate),
        deliveryStatusText: `Delivered ${formatDate(deliveryDate)}`,
      },
    };
  };

  const bossMap = () => {
    const shipping = distribution?.shippingAndHandling;
    const estDate = shipping?.estEndDeliveryDate;
    const shipmentDate = shipping?.shipments?.shipment?.[0]?.estimatedDeliveryDate;
    const pickupDate = distribution?.lineItems?.[0]?.pickupDate;
    return {
      [FULFILLMENT_STATUSES.BEING_PROCESSED]: {
        statusText: 'Ready for Pickup',
        date: formatDisplayDate(estDate),
        deliveryStatusText: `Pickup by ${formatDate(estDate)}`,
      },
      [FULFILLMENT_STATUSES.ON_ITS_WAY_TO_STORE]: {
        statusText: 'Ready for Pickup',
        date: formatDisplayDate(shipmentDate),
        deliveryStatusText: `Pickup by ${formatDate(shipmentDate)}`,
      },
      [FULFILLMENT_STATUSES.READY_FOR_PICKUP]: {
        // This can be updated when we have the correct field for estimated pickup date
        statusText: 'Available until',
        date: formatDisplayDate(pickupDate),
        deliveryStatusText: `Ready for Pickup ${formatDate(pickupDate)}`,
      },
    };
  };

  const bopisMap = () => {
    const pickupDate = distribution?.distributionDate;
    return {
      [FULFILLMENT_STATUSES.BEING_PROCESSED]: {
        statusText: 'Order Received',
        date: formatDisplayDate(pickupDate),
        deliveryStatusText: `Order Received ${formatDate(pickupDate)}`,
      },
      [FULFILLMENT_STATUSES.ON_ITS_WAY_TO_STORE]: {
        statusText: 'Arriving at Store',
        date: formatDisplayDate(pickupDate),
        deliveryStatusText: `Arriving at Store ${formatDate(pickupDate)}`,
      },
      [FULFILLMENT_STATUSES.READY_FOR_PICKUP]: {
        statusText: 'Available Until',
        date: formatDisplayDate(pickupDate),
        deliveryStatusText: `Ready for Pickup ${formatDate(pickupDate)}`,
      },
    };
  };

  const bodfsMap = () => {
    const { start, end } = getBODFSReservationWindow(distribution);
    return {
      [FULFILLMENT_STATUSES.BEING_PROCESSED]: {
        statusText: 'Order Received',
        date: formatDisplayDate(distribution?.distributionDate),
        deliveryStatusText: `Order Received ${formatDate(distribution?.distributionDate)}`,
      },
      [FULFILLMENT_STATUSES.ON_SCHEDULE]: {
        statusText: 'Estimated Arrival',
        date: formatDisplayDate(start),
        timeWindow: formatDisplayTimeWindow(start, end),
        deliveryStatusText: `Arriving By ${formatDate(start)}`,
      },
      [FULFILLMENT_STATUSES.SHIPPED]: {
        statusText: 'Arriving',
        date: formatDisplayDate(start),
        deliveryStatusText: `Arriving By ${formatDate(start)}`,
      },
      [FULFILLMENT_STATUSES.OUT_FOR_DELIVERY]: {
        statusText: 'Arriving',
        date: formatDisplayDate(start),
        deliveryStatusText: 'Out For Delivery',
      },
    };
  };

  const applianceMap = () => {
    const estimatedDeliveryDate = getApplianceDeliveryDate(distribution);
    return {
      [FULFILLMENT_STATUSES.BEING_PROCESSED]: {
        statusText: 'Estimated Arrival',
        date: formatDisplayDate(estimatedDeliveryDate),
        deliveryStatusText: `Arriving By ${formatDate(estimatedDeliveryDate)}`,
      },
      [FULFILLMENT_STATUSES.OUT_FOR_DELIVERY]: {
        statusText: 'Estimated Arrival',
        date: formatDisplayDate(estimatedDeliveryDate),
        deliveryStatusText: `Arriving By ${formatDate(estimatedDeliveryDate)}`,
      },
    };
  };

  const miscMap = () => ({
    statusText: 'Miscellaneous',
    deliveryStatusText: 'Miscellaneous',
  });

  const fulfillmentTypeMap: { [key: string]: any } = {
    [FULFILLMENT_TYPES.SHIP_TO_HOME]: shipToHomeMap(),
    [FULFILLMENT_TYPES.BUY_ONLINE_SHIP_TO_STORE]: bossMap(),
    [FULFILLMENT_TYPES.BUY_ONLINE_PICKUP_IN_STORE]: bopisMap(),
    [FULFILLMENT_TYPES.WILL_CALL_FULFILLMENT]: bopisMap(),
    [FULFILLMENT_TYPES.BUY_ONLINE_DELIVER_FROM_STORE]: bodfsMap(),
    [FULFILLMENT_TYPES.APPLIANCE]: applianceMap(),
    [FULFILLMENT_TYPES.MISC]: miscMap(),
  };

  const fulfillmentTypeStatusMap = fulfillmentTypeMap?.[fulfillmentType];
  return (
    fulfillmentTypeStatusMap?.[statusDescription]
    || (fulfillmentType === FULFILLMENT_TYPES.MISC ? fulfillmentTypeStatusMap : defaultStatus)
  );
};
